import React, { type PropsWithChildren, type MouseEvent, useCallback, useState } from "react";

import { SupIconButton, SupButton } from "@repo/core-ui";
import { classNames, notifyMessage } from "@/utils";
import { SupCopy } from "@repo/core-ui";

interface UiCopyProps {
  text: string;
  message?: string;
  copyAction?: () => void;
  className?: string;
  controlState?: boolean;
  variant?: "contained" | "outlined" | "text";
  textClassName?: string;
}

export default function UiCopy({
  text,
  controlState = false,
  message = undefined,
  copyAction = undefined,
  className = undefined,
  children,
  variant = "contained",
  textClassName,
}: PropsWithChildren<UiCopyProps>) {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const handleCopy = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      // Call the copy action if it exists
      copyAction?.();

      await navigator.clipboard.writeText(text);
      if (controlState) {
        setIsCopied(true);
      }
      const displayMessage = message ? message : `”${text}” copied to clipboard`;
      notifyMessage("Copied to clipboard successfully", displayMessage, "success", <SupCopy />);
    },
    [text, message, copyAction, controlState]
  );

  return children ? (
    <SupButton variant={variant} disabled={isCopied} className={classNames(isCopied ? "inactive" : "toggle", className)} textClassName={textClassName} onClick={handleCopy}>
      {children}
    </SupButton>
  ) : (
    <SupIconButton
      disabled={isCopied}
      active={isCopied}
      activeClassName="fill-primary-200"
      className={classNames(className, isCopied ? "inactive" : "toggle")}
      onClick={handleCopy}
      onFocus={e => e.stopPropagation()}
    >
      <SupCopy className="h-[20px] w-[20px] lg:group-hover:scale-125" />
    </SupIconButton>
  );
}
